import React, { useState } from "react";
import { Loader2, ArrowRight } from "lucide-react";
import MarketingConsent from "../MarketingConsent";
import LeadSourceField from "../LeadSourceField";
import { emptyLeadSource, leadSourceError, leadSourcePayload } from "../../lib/leadSource";

const field = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-orange/40 focus:border-orange transition-colors";

export const PackageContact = ({ onSubmit, busy, error, heading, intro }) => {
  const [form, setForm] = useState({ full_name: "", business_name: "", email: "", phone: "", message: "", marketing_opt_in: false, hp: "" });
  const [leadSource, setLeadSource] = useState(emptyLeadSource);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [sourceError, setSourceError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.full_name.trim() && /\S+@\S+\.\S+/.test(form.email);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!valid) return;
    const msg = leadSourceError(leadSource, sourceOptions, sourceOptions.length > 0);
    setSourceError(msg);
    if (msg) {
      document.querySelector('[data-testid="package-lead-source-select"]')?.focus();
      return;
    }
    onSubmit({ ...form, lead_source: leadSourcePayload(leadSource) });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8"
      data-testid="package-contact-form"
    >
      <h3 className="font-display font-bold text-navy text-xl">{heading || "Where shall we send it?"}</h3>
      <p className="text-sm text-slate-500 mt-2 leading-relaxed">
        {intro || "Leave your details and we'll come back to you with a proper quote built on the answers you've just given."}
      </p>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5">Your name *</label>
          <input className={field} data-testid="package-contact-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5">Business name</label>
          <input className={field} data-testid="package-contact-business" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5">Email *</label>
          <input type="email" className={field} data-testid="package-contact-email" value={form.email} onChange={(e) => set("email", e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5">Phone</label>
          <input className={field} data-testid="package-contact-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5">Anything else we should know?</label>
        <textarea rows={3} className={field} data-testid="package-contact-message" value={form.message} onChange={(e) => set("message", e.target.value)} />
      </div>

      <div className="absolute left-[-9999px] top-[-9999px]" aria-hidden="true">
        <label>Do not fill this field<input type="text" tabIndex={-1} autoComplete="off" value={form.hp} onChange={(e) => set("hp", e.target.value)} /></label>
      </div>

      <MarketingConsent className="mt-5" checked={form.marketing_opt_in} onChange={(v) => set("marketing_opt_in", v)} testId="package-contact-consent" />

      <LeadSourceField
        className="mt-5"
        value={leadSource}
        onChange={(v) => { setLeadSource(v); setSourceError(""); }}
        onOptions={setSourceOptions}
        required
        error={sourceError}
        labelClassName="block text-xs font-bold uppercase tracking-wide text-slate-400"
        testId="package-lead-source"
      />

      {error && <p className="text-sm text-orange-dark font-semibold mt-4" data-testid="package-contact-error">{error}</p>}

      <button
        type="submit"
        disabled={!valid || busy}
        data-testid="package-contact-submit"
        className="mt-6 inline-flex items-center gap-2 bg-orange hover:bg-orange-dark disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5"
      >
        {busy ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Send my package <ArrowRight size={17} /></>}
      </button>
      <p className="text-xs text-slate-400 mt-4">
        We'll only use your details to talk to you about your accounting. Read our <a href="/privacy-policy" className="underline hover:text-orange">privacy policy</a>.
      </p>
    </form>
  );
};

export default PackageContact;
